import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface Message {
  id: string;
  sender: string;
  senderAvatar?: string;
  content: string;
  timestamp: string;
  isOwn: boolean;
}

export interface Conversation {
  id: string;
  name: string;
  type: 'direct' | 'group';
  avatar?: string;
  unreadCount: number;
  messages: Message[];
}

interface ChatStore {
  conversations: Conversation[];
  selectedConversationId: string | null;

  // Actions
  selectConversation: (id: string | null) => void;
  addMessage: (conversationId: string, message: Message) => void;
  getSelectedConversation: () => Conversation | undefined;
  resetToInitialState: () => void;
}

const initialConversations: Conversation[] = [
  {
    id: '1',
    name: 'Alex',
    type: 'direct',
    avatar: '/avatars/alex.jpg',
    unreadCount: 0,
    messages: [
      {
        id: '1',
        sender: 'Alex',
        senderAvatar: '/avatars/alex.jpg',
        content: 'Did they really delete everything?',
        timestamp: '9:23am',
        isOwn: false,
      },
      {
        id: '2',
        sender: 'Alex',
        senderAvatar: '/avatars/alex.jpg',
        content:
          'Anyways, for your first oncall, just make sure Laura does not show a flower.',
        timestamp: '9:24am',
        isOwn: false,
      },
      {
        id: '3',
        sender: 'Alex',
        senderAvatar: '/avatars/alex.jpg',
        content: 'Force set S to 20 and L to 80 if you see a flower.',
        timestamp: '9:24am',
        isOwn: false,
      },
      {
        id: '4',
        sender: 'You',
        senderAvatar: '/avatars/y.jpg',
        content: 'What about the other two?',
        timestamp: '9:33am',
        isOwn: true,
      },
      {
        id: '5',
        sender: 'Alex',
        senderAvatar: '/avatars/alex.jpg',
        content:
          "Those we haven't figured out how to force set yet, just leave them as is.",
        timestamp: '9:33am',
        isOwn: false,
      },
      {
        id: '6',
        sender: 'You',
        senderAvatar: '/avatars/y.jpg',
        content: "I don't even see how to force set S and L.",
        timestamp: '9:43pm',
        isOwn: true,
      },
      {
        id: '7',
        sender: 'Alex',
        senderAvatar: '/avatars/alex.jpg',
        content: 'You can only set T, just find out what T should be from λ.',
        timestamp: '12:53pm',
        isOwn: false,
      },
      {
        id: '8',
        sender: 'You',
        senderAvatar: '/avatars/y.jpg',
        content: "What's λ?",
        timestamp: '12:54pm',
        isOwn: true,
      },
      {
        id: '9',
        sender: 'You',
        senderAvatar: '/avatars/y.jpg',
        content: '?',
        timestamp: '2:55pm',
        isOwn: true,
      },
    ],
  },
];

export const useChatStore = create<ChatStore>()(
  persist(
    (set, get) => ({
      conversations: initialConversations,
      selectedConversationId: initialConversations[0]?.id ?? null,

      selectConversation: (id) =>
        set({
          selectedConversationId: id,
        }),

      addMessage: (conversationId, message) =>
        set((state) => ({
          conversations: state.conversations.map((conv) =>
            conv.id === conversationId
              ? {
                  ...conv,
                  messages: [...conv.messages, message],
                }
              : conv,
          ),
        })),

      getSelectedConversation: () => {
        const state = get();
        return state.conversations.find(
          (conv) => conv.id === state.selectedConversationId,
        );
      },

      resetToInitialState: () =>
        set({
          conversations: initialConversations,
          selectedConversationId: initialConversations[0]?.id ?? null,
        }),
    }),
    {
      name: 'chat-store',
    },
  ),
);
